import { Injectable } from '@angular/core';
import {
  createDefaultPaymentSessionState,
  PaymentSessionState,
} from '../models/payment-session.model';

/**
 * PaymentSessionService
 *
 * Owns the payment-tab state machine (see PaymentSessionState) for the
 * Payment Total Dialog. Provided in PaymentTotalDialogComponent's own
 * `providers: []` — scoped to the dialog's lifecycle, matching today's
 * behavior where DashboardComponent.closeTotalDialog() explicitly resets
 * these fields on dialog close.
 *
 * Methods here are the ones that were fully self-contained (no dependency
 * on cross-cutting DashboardComponent state like scanner/credit-fetch/
 * balance). Orchestration methods that DO cross those boundaries
 * (setActivePaymentTab, onPartPaymentToggle, onPaymentOptionChange) remain
 * on DashboardComponent — see the plan's categorization — but now read/
 * write this service's state transparently through DashboardComponent's
 * getter/setter properties (self-encapsulated fields), so their bodies are
 * unchanged.
 */
@Injectable()
export class PaymentSessionService {
  state: PaymentSessionState = createDefaultPaymentSessionState();

  isSelected(paymentType: string): boolean {
    // Handle "Redeem Credit", "Add to Credit", and "Redeem Points" as the same option
    if (
      paymentType === 'Redeem Credit' ||
      paymentType === 'Add to Credit' ||
      paymentType === 'Redeem Points'
    ) {
      if (Array.isArray(this.state.selectedPaymentOptions)) {
        return (
          this.state.selectedPaymentOptions.includes('Redeem Credit') ||
          this.state.selectedPaymentOptions.includes('Add to Credit') ||
          this.state.selectedPaymentOptions.includes('Redeem Points')
        );
      } else if (typeof this.state.selectedPaymentOptions === 'string') {
        return (
          this.state.selectedPaymentOptions === 'Redeem Credit' ||
          this.state.selectedPaymentOptions === 'Add to Credit' ||
          this.state.selectedPaymentOptions === 'Redeem Points'
        );
      }
    }

    if (Array.isArray(this.state.selectedPaymentOptions)) {
      return this.state.selectedPaymentOptions.includes(paymentType);
    } else if (typeof this.state.selectedPaymentOptions === 'string') {
      return this.state.selectedPaymentOptions === paymentType;
    }
    return false;
  }

  resetCardDetails(): void {
    this.state.cardDetails = {
      name: null,
      number: 0,
      expiry: null,
      amount: null,
      transactionId: null,
    };
  }

  resetUpiDetails(): void {
    this.state.upiDetails = {
      upiId: null,
      upiAmount: 0,
      transactionId: null,
    };
  }

  /** Just the state reset — caller (DashboardComponent.resetCashDetails())
   * still calls calculateBalance() afterward, matching the original. */
  resetCashAmount(): void {
    this.state.cashAmount = 0;
  }

  /** Just the state reset — caller (DashboardComponent.resetRedeemCreditDetails())
   * still calls calculateBalance() afterward, matching the original. */
  resetRedeemCreditFields(isExchangeOrder: boolean): void {
    // Don't clear selectedInvoiceNo in exchange mode - it's needed to track which invoice is being exchanged
    if (!isExchangeOrder) {
      this.state.selectedInvoiceNo = '';
    }
    this.state.redeemCreditAmount = 0;
    this.state.availableCreditAmount = 0;
  }

  resetPartPaymentStates(): void {
    this.state.isPartPaymentCash = false;
    this.state.isPartPaymentCard = false;
    this.state.isPartPaymentUPI = false;
  }

  /** Full reset to defaults — used by DashboardComponent.closeTotalDialog(). */
  resetAll(): void {
    this.state = createDefaultPaymentSessionState();
  }

  // Sync payment values between individual and split modes
  syncPaymentValues(): void {
    if (this.state.activePaymentTab === 'Split') {
      this.state.cashAmount = this.state.splitPaymentAmounts.cash;
      this.state.cardDetails.amount = this.state.splitPaymentAmounts.card;
      this.state.upiDetails.upiAmount = this.state.splitPaymentAmounts.upi;
    } else {
      this.state.splitPaymentAmounts.cash = this.state.cashAmount || 0;
      this.state.splitPaymentAmounts.card = this.state.cardDetails.amount || 0;
      this.state.splitPaymentAmounts.upi = this.state.upiDetails.upiAmount || 0;
    }
  }

  /** Just the state mutation — caller (DashboardComponent.onSplitAmountChange())
   * still calls calculateBalance() afterward, matching the original. */
  onSplitAmountChange(): void {
    if (
      this.state.splitPaymentAmounts.cash !== null &&
      this.state.splitPaymentAmounts.cash !== undefined
    ) {
      this.state.splitPaymentAmounts.cash = Math.round(this.state.splitPaymentAmounts.cash);
    }
    if (
      this.state.splitPaymentAmounts.card !== null &&
      this.state.splitPaymentAmounts.card !== undefined
    ) {
      this.state.splitPaymentAmounts.card = Math.round(this.state.splitPaymentAmounts.card);
    }
    if (
      this.state.splitPaymentAmounts.upi !== null &&
      this.state.splitPaymentAmounts.upi !== undefined
    ) {
      this.state.splitPaymentAmounts.upi = Math.round(this.state.splitPaymentAmounts.upi);
    }

    this.state.cashAmount = this.state.splitPaymentAmounts.cash;
    this.state.cardDetails.amount = this.state.splitPaymentAmounts.card;
    this.state.upiDetails.upiAmount = this.state.splitPaymentAmounts.upi;
  }
}
